import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CreateSectionBodyDto, Section, SectionObjectType, UpdateSectionBodyDto } from '@nusign/picflow-backend-types';
import { BehaviorSubject, Observable, Subject, filter, map, of, switchMap, take, tap } from 'rxjs';
import { standardMemoryCacheExpiry } from '../consts';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { defaultAssetSection, defaultGallerySection } from '../lib/default-asset-section';
import { IGNORE_ALL_ERRORS, IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { GalleryPasswordService } from './gallery-password.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SocketService } from './socket.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class SectionService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private tenantService = inject(TenantService);
  private socketService = inject(SocketService);
  private sectionUpdateTimeMap = new Map<string, number>();

  private onSectionCreated$ = new Subject<Section>();
  private onSectionCreated = this.onSectionCreated$.subscribe(section => {
    if (!section) return;
    this.loader.update<Section[]>(`sections_${section.gallery}`, sections => {
      if (!sections) return;
      if (sections.some(s => s.id === section.id)) return sections;
      return this.fixSections([...sections, section]);
    });
  });

  public onSectionUpdated$ = new Subject<Section>();
  private onSectionUpdated = this.onSectionUpdated$.subscribe(section => {
    if (!section) return;
    this.loader.update<Section[]>(`sections_${section.gallery}`, sections => {
      if (!sections) return;
      const index = sections.findIndex(s => s.id === section.id);
      if (index === -1) return sections;
      const lastLocalUpdate = this.sectionUpdateTimeMap.get(section.id);
      if (lastLocalUpdate && lastLocalUpdate > Date.now()) return;
      const oldSection = sections[index];
      if (oldSection.updated_at > section.updated_at) return sections;
      sections[index] = section;
      return this.fixSections(sections);
    });
  });

  public onSectionDeleted$ = new Subject<Section>();
  private onSectionDeleted = this.onSectionDeleted$.subscribe(section => {
    if (!section) return;
    this.loader.update<Section[]>(`sections_${section.gallery}`, sections => {
      if (!sections) return;
      return sections.filter(s => s.id !== section.id);
    });
  });

  optimisticUpdates = new BehaviorSubject<{
    id: string;
    galleryId: string;
    params: UpdateSectionBodyDto;
  }>(undefined);
  private sectionOptimisticUpdateWatcher = this.optimisticUpdates.subscribe(data => {
    if (!data) return;
    const { id, galleryId, params } = data;
    this.loader.update<Section[]>(`sections_${galleryId}`, _sections => {
      if (!_sections) return;
      const sections = _sections.slice();
      const index = sections.findIndex(g => g.id === id);
      if (index === -1) return;
      sections[index] = { ...sections[index], ...params };
      return sections;
    });
  });

  public sectionHeightCache: { [sectionId: string]: number } = {};

  constructor() {
    this.socketService.onSocketEvent.subscribe(event => {
      if (!event) return;

      const rawSection = event.payload?.section as Section;
      if (!rawSection) return;
      const section = this.fixSections([rawSection])[0];

      if (event.name === 'section.deleted') {
        this.onSectionDeleted$.next(section);
      }

      if (event.name === 'section.created') {
        this.onSectionCreated$.next(section);
      }

      if (event.name === 'section.updated') {
        this.onSectionUpdated$.next(section);
      }
    });
  }

  public getSections(galleryId: string, options: { ignore401Errors?: boolean } = {}) {
    return this.loader.get(
      `sections_${galleryId}`,
      () => {
        return this.http
          .get<{
            data: Section[];
          }>(
            addGalleryPassword(
              `${this.config.apiEndpoint}/sections?gallery=${galleryId}`,
              this.galleryPasswordService.getPassword(galleryId)
            ),
            {
              context: new HttpContext().set(IGNORE_AUTH_ERRORS, options.ignore401Errors),
            }
          )
          .pipe(
            filter(Boolean),
            map(resp => this.fixSections(resp.data))
          );
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  public createSection(
    galleryId: string,
    type: SectionObjectType,
    position: number,
    ignoreErrors = false
  ): Observable<Section> {
    let params = <CreateSectionBodyDto>{ gallery: galleryId, object: type, position };
    if (type === 'asset') params = { ...params, ...defaultAssetSection };
    if (type === 'gallery') params = { ...params, ...defaultGallerySection };
    return this.createSectionRaw(params, ignoreErrors);
  }

  public createSectionRaw(params: CreateSectionBodyDto, ignoreErrors = false): Observable<Section> {
    return this.http
      .post<Section>(`${this.config.apiEndpoint}/sections`, params, {
        context: new HttpContext().set(IGNORE_ALL_ERRORS, ignoreErrors),
      })
      .pipe(tap(section => this.onSectionCreated$.next(section)));
  }

  public getOrCreateSection(
    sectionType: SectionObjectType,
    galleryId: string,
    parentSectionId?: string
  ): Observable<Section> {
    return this.getSections(galleryId).pipe(
      take(1),
      switchMap(sections => {
        const section = sections.find(s => {
          if (parentSectionId) return s.id === parentSectionId;
          return s.object === sectionType;
        });
        if (section) return of(section);

        const position = sections.length ? sections.slice().pop().position + 1 : 0;
        return this.createSection(galleryId, sectionType, position);
      })
    );
  }

  public optimisticUpdateSection(section: Section, params: UpdateSectionBodyDto) {
    this.sectionUpdateTimeMap.set(section.id, Date.now() + 1000);
    this.optimisticUpdates.next({ id: section.id, galleryId: section.gallery, params });
  }

  public updateSection(section: Section, params: Partial<CreateSectionBodyDto>): Observable<Section> {
    this.sectionUpdateTimeMap.set(section.id, Date.now() + 1000);
    this.optimisticUpdates.next({ id: section.id, galleryId: section.gallery, params });
    return this.http.patch<Section>(`${this.config.apiEndpoint}/sections/${section.id}`, params).pipe(
      tap(updatedSection => {
        const lastLocalUpdate = this.sectionUpdateTimeMap.get(section.id);
        if (lastLocalUpdate && lastLocalUpdate > Date.now()) return;
        this.onSectionUpdated$.next(updatedSection);
      })
    );
  }

  public deleteSection(section: Section): Observable<undefined> {
    return this.http.delete(`${this.config.apiEndpoint}/sections/${section.id}`).pipe(
      tap(() => {
        this.tenantService.refreshTenant();
        this.onSectionDeleted$.next(section);
      }),
      map(() => undefined)
    );
  }

  private fixSections(sections: Section[]) {
    return sections
      .filter(s => s.position !== 612512)
      .sort((a, b) => (a.position > b.position ? 1 : -1))
      .map(s => {
        if (!s.sorting) s.sorting = 'position';
        if (s.sorting === 'manual') {
          console.log(`Section ${s.id} has manual sorting, resetting to position sorting.`);
          s.sorting = 'position';
        }
        if (s.sorting.startsWith('random')) {
          console.log(`Section ${s.id} has random sorting: Fixing.`);
          s.sorting = 'id';
        }
        if (s.sorting.includes('asset_name')) {
          console.log(`Section ${s.id} has asset_name sorting: Fixing.`);
          s.sorting = 'name';
        }
        return s;
      });
  }
}
